/**
 * Analytics Metrics Definitions
 * 
 * This file contains detailed definitions and calculation descriptions for all analytics metrics
 * displayed in dashboards and reports. These definitions are used for tooltips, documentation,
 * and user education about how metrics are calculated.
 */

export interface MetricDefinition {
    /** Short name of the metric */
    name: string;
    /** Brief user-facing description (suitable for tooltips) */
    description: string;
    /** Detailed explanation of how the metric is calculated */
    calculation: string;
    /** Optional: Additional context or caveats */
    notes?: string;
}

/**
 * Contained Conversations
 * Measures the effectiveness of the AI assistant in handling conversations without human intervention
 */
export const CONTAINED_CALLS_DEFINITION: MetricDefinition = {
    name: 'Contained Conversations',
    description: 'Action attempts that were either completed successfully or transferred due to an unsupported request.',
    calculation: 'Calculated as successful actions (Appointment Scheduled, Appointment Rescheduled, Appointment Cancelled, Check Next Appointment Successful, or Order Placed) plus conversations that were transferred only due to unsupported request types (Unsupported Appointment Type or Unsupported Customer Request). This counts conversations where the AI handled what it could, even if ultimately transferred for a valid reason.'
};

/**
 * Labor Hours Saved
 * Quantifies the time savings provided by the AI assistant to reception staff
 */
export const LABOR_HOURS_SAVED_DEFINITION: MetricDefinition = {
    name: 'Labor Hours Saved',
    description: 'Time the assistant saved your receptionists.',
    calculation: 'Calculated by summing the duration (in seconds) of all conversations where the action was completed successfully (Appointment Scheduled, Order Placed, etc.), then converting to hours (total seconds / 3600).',
    notes: 'Only counts successfully completed actions. This represents actual time that staff would have spent completing these same actions manually.'
};

/**
 * Engagements
 * Groups related conversations from the same customer into logical interaction units
 */
export const ENGAGEMENTS_DEFINITION: MetricDefinition = {
    name: 'All Conversations',
    description: "Total number of conversations with the AI assistant, excluding conversations with no caller interaction.",
    calculation: 'Count of all incoming conversations to the AI assistant. This includes conversations with no caller interaction, Immediate Transfer requests, and any other conversations that don\'t result in an action attempt (ordering or scheduling).'
};

/**
 * Repeat Callers
 * Identifies patients who successfully used the AI assistant multiple times
 */
export const REPEAT_CALLERS_DEFINITION: MetricDefinition = {
    name: 'Repeat Callers',
    description: 'Patients who booked multiple appointments with the assistant during this timeframe.',
    calculation: 'Count of unique phone numbers that completed 2 or more appointment actions (Appointment Scheduled, Appointment Rescheduled, or Appointment Cancelled) within the selected date range.',
    notes: 'A high repeat caller rate indicates strong user satisfaction and trust in the AI assistant.'
};

/**
 * Converted Callers
 * Tracks customers who changed their preference from human assistance to AI
 */
export const CONVERTED_CALLERS_DEFINITION: MetricDefinition = {
    name: 'Converted Callers',
    description: 'Patients who initially requested staff but ultimately booked with the assistant.',
    calculation: 'Count of conversations where a customer initially requested a transfer, but within the same conversation thread ended up completing a successful action (Appointment Scheduled, Order Placed, etc.) without being transferred.',
    notes: 'This metric demonstrates the AI assistant\'s ability to win over initially skeptical customers through effective interaction.'
};

/**
 * Appointment Events
 * Tracks all appointment-related actions completed by the AI assistant
 */
export const APPOINTMENT_EVENTS_DEFINITION: MetricDefinition = {
    name: 'Appointment Events',
    description: 'Total number of appointment actions completed by the assistant.',
    calculation: 'Sum of all appointment-related actions: Appointment Scheduled (new bookings), Appointment Rescheduled, Appointment Cancelled, and Check Next Appointment Successful.',
    notes: 'This is the primary metric for measuring the AI assistant\'s productivity in handling appointment-related tasks.'
};

/**
 * Max Concurrent Conversations
 * Tracks the maximum number of simultaneous conversations being handled
 */
export const MAX_CONCURRENT_CALLS_DEFINITION: MetricDefinition = {
    name: 'Max Concurrent Conversations',
    description: 'The highest number of simultaneous conversations being handled at any point during each time period.',
    calculation: 'For each time bucket, shows the peak number of conversations that were active at the same moment. This represents the maximum load experienced by the selected assistant.',
    notes: 'This metric helps identify peak volume periods for capacity planning. Only available when a single assistant is selected.'
};

/**
 * Conversation Breakdown by Event
 * Shows distribution of conversation outcomes by category
 */
export const CALL_BREAKDOWN_DEFINITION: MetricDefinition = {
    name: 'Conversation Breakdown by Event',
    description: 'Distribution of conversations by their outcome category.',
    calculation: 'Categorizes each conversation by outcome: Appointment Scheduled, Appointment Rescheduled, Appointment Cancelled, Check Next Appointment Successful, Question Answered, and Transferred. Each conversation is counted in its primary category.',
    notes: 'The "Contained" portion represents conversations handled without transfer. The "Not Contained" portion represents conversations that were transferred to staff.'
};

/**
 * Average Conversation Length
 * Measures typical duration of AI assistant interactions
 */
export const AVERAGE_CALL_LENGTH_DEFINITION: MetricDefinition = {
    name: 'Average Conversation Length',
    description: 'Average duration of conversations with the AI assistant.',
    calculation: '(Sum of all conversation durations in seconds) / (Total number of conversations) / 60 to convert to minutes.',
    notes: 'Shorter conversation lengths generally indicate efficient service. Tracked separately for contained vs. non-contained conversations.'
};

/**
 * Conversation Volume
 * Tracks temporal patterns of customer conversations
 */
export const CALL_VOLUME_DEFINITION: MetricDefinition = {
    name: 'Conversation Volume',
    description: 'Number of conversations with the AI assistant over time.',
    calculation: 'Count of all conversations grouped by hour (8 AM - 5 PM) or by selected time interval. Can be filtered by categories (e.g., Appointment Action Attempts, Order Attempts) and outcomes (e.g., Appointment Scheduled, Order Placed).',
    notes: 'Useful for identifying peak times and staffing patterns. Filterable through the Customize button to focus on specific conversation types.'
};

/**
 * Containment Rate
 * Percentage metric for AI assistant effectiveness
 */
export const CONTAINMENT_RATE_DEFINITION: MetricDefinition = {
    name: 'Containment Rate',
    description: 'Percentage of conversations successfully handled without transfer.',
    calculation: '(Number of Contained Conversations / Total Containable Conversations) × 100. Contained conversations include successful actions (Appointment Scheduled, Order Placed, etc.) plus "good" transfers (those due to Unsupported Appointment Type or Unsupported Customer Request). The denominator uses all conversations where the customer attempted an action.',
    notes: 'Industry best practice targets 70-80% containment rate. Higher rates indicate effective AI assistant configuration. This metric focuses on actionable conversations rather than all engagements.'
};

/**
 * Planned Transfers
 * Transfers that the AI assistant is programmed to execute based on defined rules
 */
export const PLANNED_TRANSFERS_DEFINITION: MetricDefinition = {
    name: 'Planned Transfers',
    description: 'Transfers the assistant is programmed to do, based on defined rules or triggers. Only includes transfers from conversations where the caller engaged with the AI.',
    calculation: 'Count of containable conversations that were Transferred with planned transfer indicators: Unsupported Appointment Type (appointment type not in the system), Unsupported Customer Request (request outside AI capabilities), or explicit transfer requests. Excludes Immediate Transfers where the caller did not engage with the AI.',
    notes: 'Examples include: unsupported appointment types, requests to speak to a human, transfers for sensitive information, or requests outside the AI\'s configured scope. These transfers are desirable and indicate proper AI boundaries.'
};

/**
 * Unplanned Transfers
 * Transfers that occur due to AI limitations, errors, or unexpected customer behavior
 */
export const UNPLANNED_TRANSFERS_DEFINITION: MetricDefinition = {
    name: 'Unplanned Transfers',
    description: 'Transfers due to AI limitations, errors, or customer behavior. Only includes conversations where the caller engaged with the AI.',
    calculation: 'Count of containable conversations that were transferred without planned transfer indicators (Unsupported Appointment Type or Unsupported Customer Request). Excludes Immediate Transfers where the caller did not engage with the AI.',
    notes: 'Examples: AI misunderstanding the caller, system errors, frustrated callers demanding human help, or conversation breakdowns. High unplanned transfer rates indicate opportunities to improve AI training or conversation flows.'
};

/**
 * Immediate Transfers
 * Conversations where the customer requested transfer right away without attempting AI assistance
 */
export const IMMEDIATE_TRANSFERS_DEFINITION: MetricDefinition = {
    name: 'Immediate Transfers',
    description: 'Conversations where the customer immediately requested to speak with staff without engaging with the AI assistant.',
    calculation: 'Count of conversations categorized as Immediate Transfer, where the customer requested a human representative at the start of the interaction before the AI could attempt to assist.',
    notes: 'Common reasons: customer preference for human interaction, lack of trust in AI, urgent matters, or previous negative AI experiences. Tracking helps understand customer adoption patterns and may indicate opportunities for improved introduction messaging.'
};

/**
 * Containable Conversations
 * Conversations where the AI assistant attempted to help the customer accomplish a task
 */
export const CONTAINABLE_CALLS_DEFINITION: MetricDefinition = {
    name: 'Containable Conversations',
    description: 'Conversations where the customer engaged with the AI assistant to attempt an action.',
    calculation: 'Count of conversations categorized as Appointment Action Attempts (healthcare) or Order Attempts (food ordering). This excludes Immediate Transfers, No Interaction conversations, and other non-action conversations.'
};

// ==================== AI ORDERING SPECIFIC DEFINITIONS ====================

/**
 * Order Place Rate (AI Ordering)
 * Measures how effectively the AI assistant converts conversations into completed orders
 */
export const ORDER_COMPLETION_RATE_DEFINITION: MetricDefinition = {
    name: 'Order Place Rate',
    description: 'Percentage of order attempts that result in a successfully completed order.',
    calculation: 'Calculated as (Orders Placed / Order Attempts) × 100. An order is counted as placed when the order was successfully submitted to the POS system. Order Attempts includes all conversations where the customer engaged with the AI to place an order.'
};

/**
 * Contained Conversations - AI Ordering Context
 * Orders and inquiries handled entirely by the AI without staff intervention
 */
export const CONTAINED_CALLS_ORDERING_DEFINITION: MetricDefinition = {
    name: 'Placed Orders',
    description: 'Order attempts where the AI successfully completed the order without transferring to staff.',
    calculation: 'Calculated as successful order completions (Order Placed with order submitted to POS) PLUS conversations that were transferred only due to Unsupported Customer Request (e.g., catering orders, complaints). This counts conversations where the AI handled what it could, even if ultimately transferred for a valid reason.'
};

/**
 * Containable Conversations - AI Ordering Context
 * Conversations where the customer intended to place an order or had an actionable request
 */
export const CONTAINABLE_CALLS_ORDERING_DEFINITION: MetricDefinition = {
    name: 'Order Attempts',
    description: 'Conversations where the customer engaged with the AI to place an order.',
    calculation: 'Count of conversations categorized as Order Attempts, where the customer began the ordering process or expressed intent to order. Excludes Immediate Transfers, No Interaction conversations, and other non-ordering conversations.'
};

/**
 * Planned Transfers - AI Ordering Context
 */
export const PLANNED_TRANSFERS_ORDERING_DEFINITION: MetricDefinition = {
    name: 'Planned Transfers',
    description: 'Transfers the AI is programmed to execute based on business rules or capability limits. Only includes transfers from Order Attempts.',
    calculation: 'Count of Order Attempt transfers with planned indicators: Unsupported Customer Request (e.g., catering, complaints), FAQ configured transfers, Call During Off Hours transfers, or explicit caller requests to speak with staff. Excludes Immediate Transfers where the caller did not engage with the AI.'
};

/**
 * Unplanned Transfers - AI Ordering Context
 */
export const UNPLANNED_TRANSFERS_ORDERING_DEFINITION: MetricDefinition = {
    name: 'Unplanned Transfers',
    description: 'Transfers due to system issues, AI limitations, or conversation breakdowns. Only includes transfers from Order Attempts.',
    calculation: 'Count of Order Attempt transfers without planned indicators. These include: POS connection errors, payment processing errors, AI Issue, speech recognition failures, conversation loops, and Customer Frustration. Excludes Immediate Transfers where the caller did not engage with the AI.'
};

/**
 * Undetermined Transfers - AI Ordering Context
 * Transfers where the reason could not be determined by generative review
 */
export const UNDETERMINED_TRANSFERS_ORDERING_DEFINITION: MetricDefinition = {
    name: 'Undetermined Transfers',
    description: 'Transfers where the specific reason could not be determined. Only includes transfers from Order Attempts.',
    calculation: 'Count of Order Attempt transfers that could not be categorized as Planned or Unplanned. The AI-powered conversation review was unable to determine the transfer reason from the conversation context. Excludes Immediate Transfers where the caller did not engage with the AI.'
};

/**
 * Total Containable Transfers - AI Ordering Context
 * Total count of transfers that occurred during order attempts
 */
export const TOTAL_CONTAINABLE_TRANSFERS_ORDERING_DEFINITION: MetricDefinition = {
    name: 'Total Containable Transfers',
    description: 'Total number of transfers that occurred during order attempts.',
    calculation: 'Sum of all transfers from Order Attempts, categorized as Planned, Unplanned, or Undetermined. This excludes Immediate Transfers where the caller did not engage with the AI ordering assistant.',
    notes: 'This represents conversations that could have been contained but were transferred. Analyzing transfer reasons helps identify opportunities to improve AI capabilities and reduce transfers.'
};

/**
 * All Conversations - AI Ordering Context
 */
export const ALL_CALLS_ORDERING_DEFINITION: MetricDefinition = {
    name: 'All Conversations',
    description: "Total conversations handled by the AI assistant, excluding those with no caller interaction. A conversation may include multiple calls from the same caller phone number within a 10-minute window.",
    calculation: 'Count of all incoming conversations. This includes conversations with No Interaction, Immediate Transfer requests, and any other conversations that don\'t result in an Order Attempt.'
};

// ==================== HEALTHCARE SCHEDULING SPECIFIC DEFINITIONS ====================

/**
 * Contained Conversations - Healthcare Scheduling Context
 */
export const CONTAINED_CALLS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Contained Conversations',
    description: 'Conversations where the AI successfully completed a scheduling action, answered a patient question, or appropriately transferred due to unsupported request types.',
    calculation: 'Successful appointment actions (Appointment Scheduled, Appointment Rescheduled, Appointment Cancelled, or Check Next Appointment Successful) plus questions answered plus conversations transferred due to Unsupported Appointment Type or Unsupported Customer Request. Appropriate transfers count as contained because the AI correctly identified requests outside its scope.'
};

/**
 * Containment Rate - Healthcare Scheduling Context
 */
export const CONTAINMENT_RATE_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Containment Rate',
    description: 'Percentage of containable conversations that were successfully contained by the AI.',
    calculation: '(Contained Conversations / Containable Conversations) × 100. Contained conversations = Completed scheduling actions (Schedule, Reschedule, Cancel, Check Appt) + Questions answered by the AI + Unsupported Appointment Type transfers + Unsupported Customer Request transfers. Containable conversations = Appointment Action Attempts + Patient questions.',
    notes: 'Higher rates indicate effective AI assistant configuration. A conversation is also considered containable if the patient asks a question, and contained if the AI successfully answers it. Unsupported transfers count as contained because the AI correctly identified and routed requests outside its scope.'
};

/**
 * Containable Conversations - Healthcare Scheduling Context
 */
export const CONTAINABLE_CALLS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Containable Conversations',
    description: 'Conversations where the patient engaged with the AI to schedule, reschedule, manage an appointment, or ask a question.',
    calculation: 'Count of conversations categorized as Appointment Action Attempts or patient questions. Excludes Immediate Transfers, No Interaction conversations, and other non-scheduling conversations.'
};

/**
 * Planned Transfers - Healthcare Scheduling Context
 */
export const PLANNED_TRANSFERS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Planned Transfers',
    description: 'Transfers the AI is programmed to execute based on practice rules or appointment type limitations. Only includes transfers from Appointment Action Attempts.',
    calculation: 'Count of Appointment Action Attempt transfers with planned indicators: Unsupported Appointment Type (specialty appointments not configured), Unsupported Customer Request (billing questions, prescription refills), or explicit patient requests to speak with staff. Excludes Immediate Transfers where the caller did not engage with the AI.'
};

/**
 * Unplanned Transfers - Healthcare Scheduling Context
 */
export const UNPLANNED_TRANSFERS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Unplanned Transfers',
    description: 'Transfers due to system issues, AI limitations, or conversation breakdowns. Only includes transfers from Appointment Action Attempts.',
    calculation: 'Count of Appointment Action Attempt transfers without planned indicators. These include: EHR connection errors, AI Issue, speech recognition failures, conversation loops, and Customer Frustration. Excludes Immediate Transfers where the caller did not engage with the AI.'
};

/**
 * Undetermined Transfers - Healthcare Scheduling Context
 * Transfers where the reason could not be determined by generative review
 */
export const UNDETERMINED_TRANSFERS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Undetermined Transfers',
    description: 'Transfers where the specific reason could not be determined. Only includes transfers from Appointment Action Attempts.',
    calculation: 'Count of Appointment Action Attempt transfers that could not be categorized as Planned or Unplanned. The AI-powered conversation review was unable to determine the transfer reason from the conversation context. Excludes Immediate Transfers where the caller did not engage with the AI.'
};

/**
 * Total Containable Transfers - Healthcare Scheduling Context
 * Total count of transfers that occurred during appointment action attempts
 */
export const TOTAL_CONTAINABLE_TRANSFERS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Total Containable Transfers',
    description: 'Total number of transfers that occurred during appointment action attempts.',
    calculation: 'Sum of all transfers from Appointment Action Attempts, categorized as Planned, Unplanned, or Undetermined. This excludes Immediate Transfers where the caller did not engage with the AI scheduling assistant.',
    notes: 'This represents conversations that could have been contained but were transferred. Analyzing transfer reasons helps identify opportunities to improve AI capabilities and reduce transfers.'
};

/**
 * All Conversations - Healthcare Scheduling Context
 */
export const ALL_CALLS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'All Conversations',
    description: "Total conversations handled by the AI assistant, excluding those with no caller interaction. A conversation may include multiple calls from the same caller phone number within a 10-minute window.",
    calculation: 'Count of all incoming conversations. This includes conversations with No Interaction, Immediate Transfer requests, and any other conversations that don\'t result in an Appointment Action Attempt.'
};

/**
 * Engagements - Healthcare Scheduling Context
 * Total conversation volume for a location
 */
export const ENGAGEMENTS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Engagements',
    description: 'Total conversations handled by the AI assistant, excluding those with no caller interaction. A conversation may include multiple calls from the same caller phone number within a 10-minute window.',
    calculation: 'Count of all conversations associated with this location during the selected date range.'
};

/**
 * Scheduled - Healthcare Scheduling Context
 * New appointments booked by the AI
 */
export const SCHEDULED_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Scheduled',
    description: 'New appointments successfully scheduled by the AI.',
    calculation: 'Count of conversations with the Appointment Scheduled tag, indicating the AI successfully booked a new appointment for the caller.'
};

/**
 * Rescheduled - Healthcare Scheduling Context
 * Appointments rescheduled by the AI
 */
export const RESCHEDULED_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Rescheduled',
    description: 'Appointments successfully rescheduled by the AI.',
    calculation: 'Count of conversations with the Appointment Rescheduled tag, indicating the AI successfully moved an existing appointment to a new date/time.'
};

/**
 * Cancelled - Healthcare Scheduling Context
 * Appointments cancelled by the AI
 */
export const CANCELLED_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Cancelled',
    description: 'Appointments successfully cancelled by the AI.',
    calculation: 'Count of conversations with the Appointment Cancelled tag, indicating the AI successfully cancelled an existing appointment.'
};

/**
 * Checked Next - Healthcare Scheduling Context
 * Conversations where the AI confirmed the next appointment
 */
export const CHECKED_NEXT_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Checked Next',
    description: 'Conversations where the AI successfully confirmed the caller\'s next appointment.',
    calculation: 'Count of conversations with the Check Next Appointment Successful tag, indicating the AI looked up and confirmed upcoming appointment details.'
};

/**
 * No Interaction - Healthcare Scheduling Context
 * Conversations with no meaningful caller engagement
 */
export const NO_INTERACTION_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'No Interaction',
    description: 'Conversations where no meaningful interaction occurred.',
    calculation: 'Count of conversations categorized as No Interaction, where the caller hung up quickly, didn\'t respond, or the system could not detect a valid request.',
    notes: 'These conversations require no staff follow-up and are counted as managed since they don\'t represent unmet demand.'
};

/**
 * Managed Conversations - Healthcare Scheduling Context
 * Conversations that were either successfully handled by the AI or had no interaction
 */
export const MANAGED_CALLS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Managed Conversations',
    description: 'Conversations that were either successfully completed by the AI or required no interaction.',
    calculation: 'Sum of conversations with successful scheduling tags (Appointment Scheduled, Appointment Rescheduled, Appointment Cancelled, Check Next Appointment Successful) plus No Interaction conversations. Represents all conversations that did not require staff intervention.',
    notes: 'This metric shows the total volume of conversations the AI fully managed without involving staff.'
};

/**
 * Raw Attempts - Healthcare Scheduling Context
 * All conversations where a scheduling action was attempted
 */
export const RAW_ATTEMPTS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Raw Attempts',
    description: 'Total conversations where the patient attempted a scheduling action.',
    calculation: 'Count of conversations tagged with Schedule Attempted, Reschedule Attempted, or Cancellation Attempted, regardless of outcome.'
};

/**
 * Non-Containable - Healthcare Scheduling Context
 * Conversations that the AI correctly identified as outside its configured scope
 */
export const NON_CONTAINABLE_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Non-Containable',
    description: 'Conversations where the patient requested something outside the AI\'s configured capabilities.',
    calculation: 'Count of conversations tagged with Unsupported Appointment Type or Unsupported Customer Request. These are conversations where the AI correctly identified the request was outside its scope.',
    notes: 'Non-containable conversations represent proper AI boundary recognition. These are not failures but correct identification of requests requiring human handling (e.g., unsupported appointment types, insurance questions, complex patient needs).'
};

/**
 * Attempts - Healthcare Scheduling Context
 * Actionable scheduling attempts (excluding non-containable conversations)
 */
export const ATTEMPTS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Attempts',
    description: 'Scheduling attempts that were actionable by the AI.',
    calculation: 'Calculated as Raw Attempts minus Non-Containable conversations. This represents the subset of appointment action attempts where the AI had the capability to complete the request.',
    notes: 'This metric focuses on conversations where the AI could reasonably be expected to succeed, providing a fairer basis for measuring AI performance.'
};

/**
 * Supported Conversations - Healthcare Scheduling Context
 * Rate of successful completions relative to attempts
 */
export const SUPPORTED_CALLS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Supported Conversations',
    description: 'Percentage of actionable attempts that were successfully completed.',
    calculation: '(Appointment Scheduled + Appointment Rescheduled + Appointment Cancelled + Check Next Appointment Successful) / Attempts, expressed as a percentage.',
    notes: 'Measures how effectively the AI completes requests it is capable of handling. A higher rate indicates better AI performance on supported request types.'
};

/**
 * Manageable Conversations - Healthcare Scheduling Context
 * Total conversations that require attention (managed plus non-containable)
 */
export const MANAGEABLE_CALLS_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Manageable Conversations',
    description: 'Total conversations that were either managed by the AI or required staff intervention.',
    calculation: 'Sum of Managed Conversations plus Non-Containable conversations. Represents the total workload across all conversations that required some form of handling.',
    notes: 'Provides a complete picture of conversation volume that needed attention, whether handled by AI or staff.'
};

/**
 * Appointments Managed - Healthcare Scheduling Context
 * Total appointments managed including multi-patient bookings
 */
export const APPOINTMENTS_MANAGED_HEALTHCARE_DEFINITION: MetricDefinition = {
    name: 'Appointments Managed',
    description: 'Total completed scheduling actions. A single conversation can result in multiple scheduling actions when patients book appointments for multiple family members.',
    calculation: 'Count of completed scheduling actions (bookings, reschedules, cancellations, appointment checks).',
    notes: 'The event count may exceed the number of conversations due to multi-patient bookings.'
};

/**
 * Complete metrics definitions object for easy lookup
 */
export const METRICS_DEFINITIONS: Record<string, MetricDefinition> = {
    // General definitions
    CONTAINED_CALLS: CONTAINED_CALLS_DEFINITION,
    CONTAINABLE_CALLS: CONTAINABLE_CALLS_DEFINITION,
    LABOR_HOURS_SAVED: LABOR_HOURS_SAVED_DEFINITION,
    ENGAGEMENTS: ENGAGEMENTS_DEFINITION,
    REPEAT_CALLERS: REPEAT_CALLERS_DEFINITION,
    CONVERTED_CALLERS: CONVERTED_CALLERS_DEFINITION,
    APPOINTMENT_EVENTS: APPOINTMENT_EVENTS_DEFINITION,
    CALL_BREAKDOWN: CALL_BREAKDOWN_DEFINITION,
    AVERAGE_CALL_LENGTH: AVERAGE_CALL_LENGTH_DEFINITION,
    CALL_VOLUME: CALL_VOLUME_DEFINITION,
    CONTAINMENT_RATE: CONTAINMENT_RATE_DEFINITION,
    PLANNED_TRANSFERS: PLANNED_TRANSFERS_DEFINITION,
    UNPLANNED_TRANSFERS: UNPLANNED_TRANSFERS_DEFINITION,
    IMMEDIATE_TRANSFERS: IMMEDIATE_TRANSFERS_DEFINITION,
    // AI Ordering specific
    ORDER_COMPLETION_RATE: ORDER_COMPLETION_RATE_DEFINITION,
    CONTAINED_CALLS_ORDERING: CONTAINED_CALLS_ORDERING_DEFINITION,
    CONTAINABLE_CALLS_ORDERING: CONTAINABLE_CALLS_ORDERING_DEFINITION,
    PLANNED_TRANSFERS_ORDERING: PLANNED_TRANSFERS_ORDERING_DEFINITION,
    UNPLANNED_TRANSFERS_ORDERING: UNPLANNED_TRANSFERS_ORDERING_DEFINITION,
    UNDETERMINED_TRANSFERS_ORDERING: UNDETERMINED_TRANSFERS_ORDERING_DEFINITION,
    TOTAL_CONTAINABLE_TRANSFERS_ORDERING: TOTAL_CONTAINABLE_TRANSFERS_ORDERING_DEFINITION,
    ALL_CALLS_ORDERING: ALL_CALLS_ORDERING_DEFINITION,
    // Healthcare Scheduling specific
    CONTAINED_CALLS_HEALTHCARE: CONTAINED_CALLS_HEALTHCARE_DEFINITION,
    CONTAINMENT_RATE_HEALTHCARE: CONTAINMENT_RATE_HEALTHCARE_DEFINITION,
    CONTAINABLE_CALLS_HEALTHCARE: CONTAINABLE_CALLS_HEALTHCARE_DEFINITION,
    PLANNED_TRANSFERS_HEALTHCARE: PLANNED_TRANSFERS_HEALTHCARE_DEFINITION,
    UNPLANNED_TRANSFERS_HEALTHCARE: UNPLANNED_TRANSFERS_HEALTHCARE_DEFINITION,
    UNDETERMINED_TRANSFERS_HEALTHCARE: UNDETERMINED_TRANSFERS_HEALTHCARE_DEFINITION,
    TOTAL_CONTAINABLE_TRANSFERS_HEALTHCARE: TOTAL_CONTAINABLE_TRANSFERS_HEALTHCARE_DEFINITION,
    ALL_CALLS_HEALTHCARE: ALL_CALLS_HEALTHCARE_DEFINITION,
    ENGAGEMENTS_HEALTHCARE: ENGAGEMENTS_HEALTHCARE_DEFINITION,
    SCHEDULED_HEALTHCARE: SCHEDULED_HEALTHCARE_DEFINITION,
    RESCHEDULED_HEALTHCARE: RESCHEDULED_HEALTHCARE_DEFINITION,
    CANCELLED_HEALTHCARE: CANCELLED_HEALTHCARE_DEFINITION,
    CHECKED_NEXT_HEALTHCARE: CHECKED_NEXT_HEALTHCARE_DEFINITION,
    NO_INTERACTION_HEALTHCARE: NO_INTERACTION_HEALTHCARE_DEFINITION,
    MANAGED_CALLS_HEALTHCARE: MANAGED_CALLS_HEALTHCARE_DEFINITION,
    RAW_ATTEMPTS_HEALTHCARE: RAW_ATTEMPTS_HEALTHCARE_DEFINITION,
    NON_CONTAINABLE_HEALTHCARE: NON_CONTAINABLE_HEALTHCARE_DEFINITION,
    // General metrics
    MAX_CONCURRENT_CALLS: MAX_CONCURRENT_CALLS_DEFINITION,
    ATTEMPTS_HEALTHCARE: ATTEMPTS_HEALTHCARE_DEFINITION,
    SUPPORTED_CALLS_HEALTHCARE: SUPPORTED_CALLS_HEALTHCARE_DEFINITION,
    MANAGEABLE_CALLS_HEALTHCARE: MANAGEABLE_CALLS_HEALTHCARE_DEFINITION,
    SCHEDULING_EVENTS_HEALTHCARE: APPOINTMENTS_MANAGED_HEALTHCARE_DEFINITION
};

/**
 * Helper function to get a metric definition by key
 */
export const getMetricDefinition = (key: string): MetricDefinition | undefined => {
    return METRICS_DEFINITIONS[key];
};

/**
 * Helper function to get just the description for tooltips
 */
export const getMetricDescription = (key: string): string => {
    return METRICS_DEFINITIONS[key]?.description || '';
};

/**
 * Helper function to get the full calculation explanation
 */
export const getMetricCalculation = (key: string): string => {
    return METRICS_DEFINITIONS[key]?.calculation || '';
};

/**
 * Helper function to format a complete tooltip with name, description, and calculation
 */
export const getMetricTooltip = (key: string, includeCalculation: boolean = false): string => {
    const definition = METRICS_DEFINITIONS[key];
    if (!definition) return '';

    let tooltip = `${definition.name}\n\n${definition.description}`;

    if (includeCalculation) {
        tooltip += `\n\nHow it's calculated:\n${definition.calculation}`;
    }

    if (definition.notes && includeCalculation) {
        tooltip += `\n\nNote: ${definition.notes}`;
    }

    return tooltip;
};
